// Core
import React from 'react';

// Libraries
import { useTheme } from 'styled-components';

// Hooks
import { useBreakpoint } from '../../../hooks/useBreakpoint/useBreakpoint';

// Layout
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../Paragraph';

// Types
import { Theme } from '../../../types/theme';

// Component
import {
  Container,
  Indicator,
  StatusDescription,
} from './StatusIndicator.style';
import {
  StatusIndicatorProps,
  StatusIndicatorSize,
  StatusIndicatorStatus,
} from './StatusIndicator.types';

function StatusIndicator({
  className,
  size = StatusIndicatorSize.sm,
  status = StatusIndicatorStatus.good,
  testId,
  text,
}: StatusIndicatorProps): JSX.Element {
  // Dependencies
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();

  // Map object to convert StatusIndicatorSize to ParagraphSize
  const sizeMap: Record<StatusIndicatorSize, ParagraphSize> = {
    [StatusIndicatorSize.sm]: ParagraphSize.sm,
    [StatusIndicatorSize.lg]: ParagraphSize.lg,
  };

  const convertSizeToParagraphSize = (): ParagraphSize => {
    if (isXs) {
      return size === StatusIndicatorSize.lg ? ParagraphSize.md : ParagraphSize.xs;
    }
    return sizeMap[size];
  };

  return (
    <Container
      data-testid={testId}
      className={className}
      text={text}
    >
      <Indicator
        data-testid="status-indicator"
        status={status}
      />
      <StatusDescription>
        <Paragraph
          testId="status-description"
          size={convertSizeToParagraphSize()}
          color={
            status === StatusIndicatorStatus.neutral
              ? theme.color.statusIndicator.neutralTextColor
              : theme.color.statusIndicator.defaultTextColor
          }
          weight={
            size === StatusIndicatorSize.lg
              ? ParagraphWeight.bold
              : ParagraphWeight.regular
          }
        >
          {text}
        </Paragraph>
      </StatusDescription>
    </Container>
  );
}

export { StatusIndicator };
